import { useState } from "react";
import { Apple, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { APP_STORE_URL, PLAY_STORE_URL, detectMobilePlatform } from "@/lib/appStoreLinks";

interface StoreBadgesProps {
  /** "lg" for a page's primary call to action, "sm" for the site banner. */
  size?: "sm" | "lg";
  className?: string;
}

/**
 * The App Store and Google Play badges, in one place.
 *
 * A phone is shown only its own store; anything else — desktops included —
 * gets both, so being wrong about a user agent costs a visitor nothing. The
 * platform is read once at mount via a lazy initializer rather than in an
 * effect: this is a client-rendered SPA with no server markup to match, and
 * deferring it made the badges visibly rearrange after first paint.
 */
export function StoreBadges({ size = "sm", className }: StoreBadgesProps) {
  const [platform] = useState(() => detectMobilePlatform());

  const showApple = platform === "ios" || platform === "other";
  const showPlay = platform === "android" || platform === "other";
  const large = size === "lg";

  return (
    <div
      className={cn(
        "flex items-center gap-3",
        large && "flex-col sm:flex-row sm:justify-center",
        className,
      )}
    >
      {showApple && (
        <StoreBadge
          href={APP_STORE_URL}
          icon={<Apple className={large ? "h-6 w-6" : "h-5 w-5"} aria-hidden="true" />}
          lead="Download on the"
          name="App Store"
          large={large}
        />
      )}
      {showPlay && (
        <StoreBadge
          href={PLAY_STORE_URL}
          icon={<Play className={large ? "h-6 w-6" : "h-5 w-5"} aria-hidden="true" />}
          lead="Get it on"
          name="Google Play"
          large={large}
        />
      )}
    </div>
  );
}

function StoreBadge({
  href,
  icon,
  lead,
  name,
  large,
}: {
  href: string;
  icon: React.ReactNode;
  lead: string;
  name: string;
  large: boolean;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        // bg-foreground, not bg-card/70: over a card the translucent card
        // colour composites to exactly the card's own background, so the
        // badge had no fill and its hover state changed nothing. The store
        // badges are the primary call to action and have to read as buttons.
        "flex items-center gap-3 rounded-lg bg-foreground text-background transition-opacity hover:opacity-90",
        large ? "flex-1 justify-center px-5 py-3" : "px-4 py-2",
      )}
    >
      {icon}
      <div className="text-left">
        <p className="text-xs leading-none opacity-70">{lead}</p>
        <p className={cn("font-semibold leading-tight", large ? "text-sm" : "text-xs")}>{name}</p>
      </div>
      <span className="sr-only">(opens in a new tab)</span>
    </a>
  );
}
